import Rails from '@rails/ujs';
import { Controller } from 'stimulus';

const TASK_DROPDOWN_DISABLED_CLASSES = ['opacity-50', 'pointer-events-none', 'cursor-not-allowed'];

export default class extends Controller {
  static values = {
    overviewUrl: String,
    programUrl: String,
    responsesUrl: String,
    learnersUrl: String
  };

  static targets = [
    'link',
    'section',
    'taskContainer',
    'taskMenuItem',
    'responsesSummaryView',
    'learnerResponsesView'
  ];

  connect() {
    if (!this.hasSectionTarget) return;

    const pageId = window.location.hash.slice(1);
    const target = this.sectionTargets.find((section) => section.dataset.pageId === pageId);

    this.setActiveLink(target ? pageId : this.sectionTargets[0]?.dataset.pageId);
    target?.scrollIntoView({ block: 'start' });
  }

  // Picking a task moves to that task's own address rather than submitting the
  // filters as query parameters, so the address describes what is on screen.
  submit(event) {
    const taskId = event.target.value;

    // The prompt entry undoes a pick, it is not a place to go. Only the program
    // switch below takes the screen somewhere when the task is cleared.
    if (!taskId) return;

    window.location.assign(this.viewUrl(taskId));
  }

  programOnlyUrl() {
    // By name, not by id: the dropdown puts the same id on its container, which
    // has no value of its own.
    const programId = this.element.querySelector('input[name="program_id"]')?.value;

    if (!programId) return this.overviewUrlValue;

    return this.programUrlValue.replace('PROGRAM_ID', programId);
  }

  viewUrl(taskId) {
    const template = this.isLearnerResponsesView() ?
      this.learnersUrlValue :
      this.responsesUrlValue;

    return template.replace('TASK_ID', taskId);
  }

  isLearnerResponsesView() {
    return this.hasLearnerResponsesViewTarget &&
      !this.learnerResponsesViewTarget.classList.contains('hidden');
  }

  // Filters the task dropdown to the chosen program client-side, without
  // reloading the page - the task list is already fully loaded, so there's
  // nothing the server needs to compute just from a program pick.
  programChanged(event) {
    if (!this.hasTaskContainerTarget) return;

    const programId = event.target.value;

    this.taskMenuItemTargets.forEach((item) => {
      item.classList.toggle('hidden', item.dataset.programId !== programId);
    });

    TASK_DROPDOWN_DISABLED_CLASSES.forEach((className) => {
      this.taskContainerTarget.classList.toggle(className, !programId);
    });

    const taskDropdown = this.application.getControllerForElementAndIdentifier(
      this.taskContainerTarget.firstElementChild,
      'components--admin-dropdowns-dropdown'
    );
    const selectedTaskId = taskDropdown?.hiddenFieldTarget?.value;

    if (!selectedTaskId) return;

    const selectedTaskStillVisible = this.taskMenuItemTargets.some(
      (item) => item.dataset.value === selectedTaskId && item.dataset.programId === programId
    );

    if (selectedTaskStillVisible) return;

    taskDropdown.resetSelection();

    // The task in the address belongs to the program just left behind, so the
    // address moves to the program on its own. Dropping the program as well
    // would undo the pick the admin just made.
    window.location.assign(this.programOnlyUrl());
  }

  // Switches between the two overview sub-views without a full reload. Each
  // view is fetched once and cached in the DOM (data-loaded) - switching back
  // to an already-loaded view just toggles visibility, no request involved.
  viewChanged(event) {
    const url = event.detail.value;
    const isLearnerResponses = url.includes('/learners');
    const target = isLearnerResponses ?
      this.learnerResponsesViewTarget :
      this.responsesSummaryViewTarget;
    const other = isLearnerResponses ?
      this.responsesSummaryViewTarget :
      this.learnerResponsesViewTarget;

    other.classList.add('hidden');
    target.classList.remove('hidden');

    history.pushState({}, '', url.replace(/\.js$/, ''));

    if (target.dataset.loaded === 'true') return;

    Rails.ajax({ url, type: 'GET', dataType: 'script' });
  }

  navigateToSection(event) {
    event.preventDefault();

    const pageId = event.currentTarget.dataset.pageId;
    const target = this.sectionTargets.find((section) => section.dataset.pageId === pageId);

    this.setActiveLink(pageId);
    target?.scrollIntoView({ block: 'start' });

    // Replaces rather than pushes: jumping between pages of the same summary is
    // not somewhere the back button should have to walk through.
    history.replaceState({}, '', `${window.location.pathname}#${pageId}`);
  }

  // The section is marked too, so every question card belonging to the picked
  // page is highlighted, not just the one the scroll lands on.
  setActiveLink(pageId) {
    this.linkTargets.forEach((link) => {
      link.classList.toggle('active', link.dataset.pageId === pageId);
    });

    this.sectionTargets.forEach((section) => {
      section.classList.toggle('active', section.dataset.pageId === pageId);
    });
  }
}
